/**
 * LeftPanel Component - Action and Tool Management Sidebar
 * 
 * This component provides the left sidebar for the BattleRoom, containing panels
 * for combat actions and drawing tools used during D&D sessions.
 * 
 * PANELS INCLUDED:
 * - Actions Panel: Shows available combat actions for the currently selected character/monster
 * - Drawing Tools: Provides tools for drawing on the map (lines, shapes, measurements)
 * 
 * SMART BEHAVIOR:
 * - Actions Panel only appears when a combatant (player/monster/NPC) is selected
 * - Map assets show "No actions available" message since maps don't have combat actions
 * - Uses CollapsiblePanel for space-efficient expandable sections
 * - Automatically updates when different tokens are selected
 * 
 * INTEGRATION:
 * - Receives currentCombatant from BattleRoom (the selected token)
 * - Filters out map assets to prevent showing actions for non-combatants
 * - Calls onActionTaken when user performs combat actions
 * - Works with the Initiative Tracker to coordinate turn-based combat
 */

import React, { useMemo, useCallback, useRef } from 'react';
import { Bug, Crosshair, ListOrdered, RefreshCw, SlidersHorizontal, Swords } from 'lucide-react';
import { DEBUG_ENABLED } from '../../../utils/DebugLogger';
import InitiativeTracker from '../../InitiativeTracker/InitiativeTracker';
import ActionTracker from './ActionTracker';
import GMPanel from './GMPanel';
import ResizablePanel from './ResizablePanel';
import MultiattackTargetingPopup from '../../MultiattackTargetingPopup/MultiattackTargetingPopup';
import { buildCombatActionCatalog } from '../../../utils/combat/CombatActionCatalog';
import './LeftPanel.css';
import NotificationManager from '../../../utils/NotificationManager';

const LeftPanel = ({ 
  currentCombatant, 
  onActionTaken,
  onFamiliarAction,
  onCombatantChange,
  onTokenSelect,
  tokens,
  allTokens = tokens,
  round = 1,
  combatStarted = false,
  initiativeExternallyManaged = false,
  externalInitiativeState = null,
  authoritativeInitiative = null,
  // Integration props passed from BattleRoom for ActionTracker wiring
  selectedTokenId,
  actionsUsed,
  // Targeting integration
  armedAction,
  targetingEnabled,
  selectedTargets = [],
  selectedTargetsCount,
  onArmAction,
  onConfirmArmed,
  onCancelArmed,
  // Multiattack sequence integration
  multiattackSequence,
  multiattackTarget,
  isInMultiattackMode,
  onExecuteMultiattackComponent,
  onSkipMultiattackComponent,
  // @date_of_change 2026-07-11 - Grid/token context for the pinned targeting popup's
  // advantage/Sneak Attack preview
  previewCombatState = null,
  // @date_of_change 2025-09-06 - Pinned Multiattack Targeting popup integration
  pinnedMultiattackTargeting,
  onMultiattackTargetingClose,
  onMultiattackTargetingUpdate,
  onMultiattackTargetingTogglePin,
  onStartMultiattack,
  onAddToMultiattack,
  onExecuteMultiattack,
  onClearMultiattack,
  // GM Panel integration
  onTokenUpdate,
  onHitPointAdjustment,
  onCombatStateChange,
  // @date_of_change 2025-09-12 - Programmatic expand when using icon mode left rail
  // Shape: { section: 'initiative' | 'actions' | 'gm', ts: number }
  expandSection,
  // @date_of_change 2025-09-15 - Combat reset integration for initiative tracker reset button
  onResetCombat,
  // @date_of_change 2025-09-24 - Multiplayer integration props
  connectedPlayers = [],
  currentUser = null,
  isGameMaster = false,
  tokenOwnership = {},
  getTokenControlInfo = () => ({ controlled: false, displayText: 'Unassigned', color: '#gray' }),
  // @date_of_change: 2025-10-05 - WebSocket initiative sync
  sendInitiativeUpdate = null,
  onLocalInitiativeChange,
  // @date_of_change: 2025-10-06 - Ownership-based action gating
  canControlToken = null,
  // @date_of_change: 2025-10-13 - Lifted dice settings to BattleRoom (per AGENTS.md: separate engine from presentation)
  diceSettings = { gmAutomaticRolls: true, playerAutomaticRolls: true },
  onDiceSettingsChange = () => {},
  // date_of_change: 2026-06-07
  // WHAT: Opens the right-side sheet panel from action selections.
  // WHY: Action resources/details should live on the character sheet, not as duplicated action-panel chrome.
  onOpenSheetPanel = null,
  gmOverride = null,
  onGmOverrideChange = null,
  onEditCombatLog = null
}) => {
  const initiativeTrackerRef = useRef(null);

  // WHAT: Route the heading-level reset icon back to the tracker that owns initiative state.
  // WHY: Reset remains a single operation while its compact control sits beside the panel title.
  // date_of_change: 2026-09-20
  const handleInitiativeReset = useCallback(() => {
    initiativeTrackerRef.current?.resetCombat?.();
  }, []);
  
  // (removed unused local handleActionTaken; ActionTracker uses handleActionTakenGuarded)

  // Handle dice roll from ActionTracker or ChatDicePanel
  const handleDiceRoll = (message, rollResult) => {
    console.log('[LeftPanel] Dice roll:', { message, rollResult });
  };

  // @date_of_change: 2025-10-09 - View/Control are based on the selected token when permitted, else fall back
  // This applies pre-combat and in combat. Players can view tokens they control or All Players; monsters stay hidden for players.
  const selectedToken = useMemo(() => {
    try {
      return tokens?.find?.(t => t.id === selectedTokenId) || null;
    } catch {
      return null;
    }
  }, [tokens, selectedTokenId]);

  const canViewToken = useCallback((token) => {
    if (!token) return false;
    if (isGameMaster) return true;
    const t = (token.type || '').toLowerCase();
    if (t === 'monster') return false;
    if (typeof canControlToken === 'function') {
      try { if (canControlToken(token.id)) return true; } catch {}
    }
    try {
      const info = getTokenControlInfo(token.id);
      return info?.displayText === 'All Players' || (info?.userId && info.userId === currentUser?.userId);
    } catch {
      return false;
    }
  }, [isGameMaster, canControlToken, getTokenControlInfo, currentUser]);

  // Prefer selected token if viewable so the GM can inspect any creature mid-combat.
  // Initiative/action execution remains keyed to currentCombatant via ActionTracker.activeCombatant.
  // date_of_change: 2026-06-23
  const displayCombatant = useMemo(() => {
    if (canViewToken(selectedToken)) return selectedToken;
    return currentCombatant || null;
  }, [selectedToken, currentCombatant, canViewToken]);

  const canViewActions = useMemo(() => {
    return canViewToken(displayCombatant);
  }, [displayCombatant, canViewToken]);

  const canControlCurrent = useMemo(() => {
    if (!displayCombatant) return false;
    if (isGameMaster) return true;
    if (typeof canControlToken === 'function') {
      return !!canControlToken(displayCombatant.id);
    }
    try {
      const info = getTokenControlInfo(displayCombatant.id);
      return info?.displayText === 'All Players' || (info?.userId && info.userId === currentUser?.userId);
    } catch {
      return false;
    }
  }, [displayCombatant, isGameMaster, canControlToken, getTokenControlInfo, currentUser]);

  const currentCombatantActions = useMemo(
    () => buildCombatActionCatalog(currentCombatant),
    [currentCombatant]
  );

  const handleActionTakenGuarded = useCallback((tokenId, action, explicitTargetIds) => {
    if (!isGameMaster && !canControlCurrent) {
      NotificationManager.warn('You cannot control this token.');
      return;
    }
    if (onActionTaken) onActionTaken(tokenId, action, explicitTargetIds);
  }, [isGameMaster, canControlCurrent, onActionTaken]);
  const handleFamiliarActionGuarded = useCallback((request) => {
    if (!isGameMaster && !canControlCurrent) {
      NotificationManager.warn('You cannot control this token.');
      return false;
    }
    return onFamiliarAction ? onFamiliarAction(request) : false;
  }, [isGameMaster, canControlCurrent, onFamiliarAction]);
  return (
    <div className={`left-panel ${!isGameMaster ? 'non-gm' : ''}`}>
      <ResizablePanel 
        title="Initiative Tracker" 
        icon={<ListOrdered size={15} strokeWidth={1.7} />}
        headerAction={isGameMaster && combatStarted && !initiativeExternallyManaged ? (
          <button
            type="button"
            className="initiative-reset-icon-button"
            onClick={handleInitiativeReset}
            aria-label="Reset combat"
            title="Reset combat and initiative order"
          >
            <RefreshCw size={15} strokeWidth={1.8} aria-hidden="true" />
          </button>
        ) : null}
        defaultExpanded={true}
        defaultHeight={250}
        minHeight={150}
        maxHeight={400}
        expandSignal={expandSection?.section === 'initiative' ? expandSection.ts : undefined}
      >
        <InitiativeTracker
          ref={initiativeTrackerRef}
          tokens={tokens}
          allTokens={allTokens}
          combatActive={combatStarted}
          onCombatantChange={onCombatantChange}
          onTokenSelect={onTokenSelect}
          // @date_of_change 2025-09-15 - Combat reset integration for initiative tracker reset button
          onResetCombat={onResetCombat}
          // @date_of_change 2025-09-24 - Multiplayer integration props
          connectedPlayers={connectedPlayers}
          currentUser={currentUser}
          isGameMaster={isGameMaster}
          tokenOwnership={tokenOwnership}
          getTokenControlInfo={getTokenControlInfo}
          canControlToken={canControlToken}
          // @date_of_change: 2025-10-05 - WebSocket initiative sync
          sendInitiativeUpdate={sendInitiativeUpdate}
          onLocalInitiativeChange={onLocalInitiativeChange}
          initiativeExternallyManaged={initiativeExternallyManaged}
          externalInitiativeState={externalInitiativeState}
          authoritativeInitiative={authoritativeInitiative}
        />
      </ResizablePanel>

      {/* @date_of_change 2025-09-10 - REMOVED: Duplicate multiattack sequence panel
        * This functionality is now handled exclusively by the MultiattackTargetingPopup component
        * to eliminate UI duplication as requested by user. The popup provides the same
        * targeting and execution functionality in a dedicated interface.
        * 
        * Original multiattack panel features moved to MultiattackTargetingPopup:
        * - Progress tracking and attack counter
        * - Target selection display  
        * - Execute/Skip/Cancel controls
        * - Current attack information display
        */}

      {/* Debug Panel - Shows multiattack state info */}
      {DEBUG_ENABLED && (multiattackSequence || isInMultiattackMode) && (
        <ResizablePanel 
          title="Multiattack Debug" 
          icon={<Bug size={15} strokeWidth={1.7} />}
          defaultExpanded={false}
          defaultHeight={150}
          minHeight={100}
          maxHeight={200}
        >
          <div className="multiattack-debug-panel" style={{ padding: '8px', fontSize: '12px', fontFamily: 'monospace' }}>
            <div>Mode: {isInMultiattackMode ? 'MULTIATTACK' : 'NORMAL'}</div>
            <div>Sequence: {multiattackSequence ? 'ACTIVE' : 'NONE'}</div>
            {multiattackSequence && (
              <>
                <div>Action: {multiattackSequence.parentAction}</div>
                <div>Components: {multiattackSequence.componentActions?.length || 0}</div>
                <div>Current: {(multiattackSequence.currentIndex || 0) + 1}</div>
                <div>Attacker: {multiattackSequence.attackerId}</div>
              </>
            )}
            <div>Target: {multiattackTarget ? multiattackTarget.name : 'NONE'}</div>
          </div>
        </ResizablePanel>
      )}

      <ResizablePanel 
        title="Actions" 
        icon={<Swords size={15} strokeWidth={1.7} />}
        defaultExpanded={true}
        defaultHeight={300}
        minHeight={200}
        maxHeight={500}
        expandSignal={expandSection?.section === 'actions' ? expandSection.ts : undefined}
      >
        {canViewActions ? (
          <ActionTracker 
            // @date_of_change: 2025-10-09 - Drive Actions panel from selected token when permitted
            currentCombatant={displayCombatant}
            allTokens={allTokens}
            onFamiliarAction={handleFamiliarActionGuarded}
            activeCombatant={currentCombatant}
            round={round}
            combatStarted={combatStarted}
            // @date_of_change: 2025-10-09 - Propagate role so ActionTracker can hide GM-only controls for players
            isGameMaster={isGameMaster}
            onActionTaken={handleActionTakenGuarded}
            onDiceRoll={handleDiceRoll}
            selectedTokenId={selectedTokenId}
            actionsUsed={actionsUsed}
            // @date_of_change 2025-09-07 - Targeting integration removed, handled by MultiattackTargetingPopup
            // Multiattack mode integration (kept for action filtering)
            multiattackSequence={multiattackSequence}
            isInMultiattackMode={isInMultiattackMode}
            // @date_of_change: 2025-10-11 - Pass token control function for ownership-based UI gating
            canControlToken={canControlToken}
            onOpenDetailsPanel={onOpenSheetPanel}
            gmOverride={gmOverride}
            onGmOverrideChange={onGmOverrideChange}
            onEditCombatLog={onEditCombatLog}
          />
        ) : (
          <div className="no-combatant">
            <p>Actions hidden for {displayCombatant?.name || 'this token'}</p>
            <small>Players cannot view monster or NPC actions.</small>
          </div>
        )}
      </ResizablePanel>

      {/* Pinned Multiattack Targeting Panel */}
      {pinnedMultiattackTargeting && pinnedMultiattackTargeting.visible && pinnedMultiattackTargeting.isPinned && (
        <ResizablePanel 
          title="Action Targeting (Pinned)" 
          icon={<Crosshair size={15} strokeWidth={1.7} />}
          defaultExpanded={true}
          defaultHeight={500}
          minHeight={300}
          maxHeight={700}
        >
          <div className="pinned-multiattack-content">
            <MultiattackTargetingPopup
              visible={true} // Always visible when pinned
              onClose={onMultiattackTargetingClose}
              onTogglePin={onMultiattackTargetingTogglePin}
              position={{ x: 0, y: 0 }} // Position doesn't matter when embedded
              isPinned={!!pinnedMultiattackTargeting?.isPinned}
              renderInline={true}
              // Pass current combatant data
              currentCombatant={currentCombatant}
              availableActions={currentCombatantActions}
              tokens={Array.isArray(allTokens) && allTokens.length > 0 ? allTokens : tokens}
              // @date_of_change 2026-07-11 - Enable advantage/Sneak Attack preview when pinned
              combatState={previewCombatState}
              // Pass targeting state
              selectedTargets={selectedTargets}
              targetingEnabled={targetingEnabled}
              armedAction={armedAction}
              // Pass targeting callbacks
              onArmAction={onArmAction}
              onConfirmArmed={onConfirmArmed}
              onCancelArmed={onCancelArmed}
              // Pass multiattack state and callbacks
              multiattackSequence={multiattackSequence}
              isInMultiattackMode={isInMultiattackMode}
              onStartMultiattack={onStartMultiattack}
              onAddToMultiattack={onAddToMultiattack}
              onExecuteMultiattack={onExecuteMultiattack}
              onClearMultiattack={onClearMultiattack}
            />
          </div>
        </ResizablePanel>
      )}

      {/* GM Adjustments Panel - Only visible to Game Master */}
      {/* @date_of_change: 2025-09-25 - Added GM-only visibility restriction */}
      {isGameMaster && (
        <ResizablePanel
          title="GM Adjustments"
          icon={<SlidersHorizontal size={15} strokeWidth={1.7} />}
          defaultExpanded={false}
          defaultHeight={350}
          minHeight={200}
          maxHeight={600}
          expandSignal={expandSection?.section === 'gm' ? expandSection.ts : undefined}
        >
          <GMPanel
            selectedToken={currentCombatant}
            tokens={tokens}
            onTokenUpdate={onTokenUpdate}
            onHitPointAdjustment={onHitPointAdjustment}
            onDiceRoll={handleDiceRoll}
            onCombatStateChange={onCombatStateChange}
            combatStarted={combatStarted}
            diceSettings={diceSettings}
            onDiceSettingsChange={onDiceSettingsChange}
          />
        </ResizablePanel>
      )}

    </div>
  );
};

export default LeftPanel;
